import { apiQueryClient } from "$src/lib/api";
import { useAccount } from "$src/stores/use-account";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

import type { BFFInput, BFFOutput } from "@tracksuit/bff/trpc";

import { useAdminSettings } from "../stores/use-admin-settings";

export const useAvailableFilters = (
  hideEmptyPopFilters?: boolean,
  namespaceInput?: "statements" | "funnel",
) => {
  const accountBrand = useAccount((s) => s.active);
  const includeIncompleteMonths = useAdminSettings(
    (s) => s.settings.includeIncompleteMonths,
  );
  const { pathname } = useLocation();
  const [demographics, setDemographics] = useState<
    NonNullable<BFFOutput["filters"]["get"]["demographics"]>
  >([]);

  const namespace: BFFInput["filters"]["get"]["namespace"] = useMemo(() => {
    if (!!namespaceInput) {
      return namespaceInput;
    }
    return pathname.startsWith("/statements") ? "statements" : "funnel";
  }, [pathname, namespaceInput]);
  const namespaceValid = useMemo(
    () =>
      namespace === "funnel"
        ? true
        : accountBrand?.featuresConfig.statementsEnabled,
    [namespace, accountBrand],
  );
  const { data, isLoading: loading } = apiQueryClient.filters.get.useQuery(
    {
      accountBrandId: Number(accountBrand?.accountBrandId),
      includeIncompleteMonths: !!includeIncompleteMonths,
      namespace,
    },
    {
      enabled: !!namespaceValid && !!accountBrand,
    },
  );

  useEffect(() => {
    setDemographics(
      data?.demographics?.length
        ? data.demographics.filter((f: any) =>
            hideEmptyPopFilters === true ? f.supportsPopulation : true,
          )
        : [],
    );
  }, [data?.demographics]);

  return {
    availableFilters: {
      dates: data?.dates,
      demographics,
      brands: [
        { id: accountBrand?.brandId, name: accountBrand?.brandName },
        ...(accountBrand?.competitors ?? []),
      ],
    },
    loading,
  };
};
